/* eslint-disable jira/naming/disallow-filenames */

/* eslint-disable import/order */
import React, { useCallback, useEffect } from 'react';
import type ServicedeskKnowledgeHubSuggestedArticlesType from '@jira/spa-apps__servicedesk-knowledge-hub-suggested-articles/src/index.tsx';
import useIsAdminHubAIEnabled from '@jira/platform__utils-react-hooks-use-is-admin-hub-ai-enabled/src/controllers/index.tsx';
import { markAsProductBundleReady } from '@jira/platform__spa/src/services/mark-as-product-bundle-ready/index.tsx';
import { LazyPage } from '@jira/platform__spa/src/view/lazy-page/index.tsx';
import { kbTopicsResource } from '@jira/router-resources__knowledge-hub/src/kbTopicsResource.tsx';
import { useSuggestedTopics } from '@jira/servicedesk__knowledge-performance/src/useSuggestedTopics.tsx';
import { usePageContext } from '@jira/servicedesk__spa-commons/src/usePageContext.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { lazyForPaint, type LazyComponent } from '@atlassian/react-loosely-lazy';
import { useResource } from '@atlassian/react-resource-router';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

export const LazyServicedeskKnowledgeHubSuggestedArticles: LazyComponent<() => React.JSX.Element> =
	lazyForPaint<typeof ServicedeskKnowledgeHubSuggestedArticlesType>(() =>
		markAsProductBundleReady(
			import(
				/* webpackChunkName: "async-servicedesk-knowledge-hub-suggested-articles", jiraSpaEntry: "async-servicedesk-knowledge-hub-suggested-articles" */ '@jira/spa-apps__servicedesk-knowledge-hub-suggested-articles/src/index.tsx'
			),
		),
	);

export const ServiceDeskKnowledgeHubSuggestedArticles = (): React.JSX.Element => {
	const {
		data: isAdminHubAIEnabled,
		loading: isAdminHubAIEnabledLoading,
		error: isAdminHubAIEnabledError,
		fetchAIEnablement,
	} = useIsAdminHubAIEnabled('jira-servicedesk');

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ data: pageContextData }] = usePageContext();
	const { projectContext, tenantContext } = pageContextData || {};
	const { projectId } = projectContext || {};
	const activationId = tenantContext?.activationId || '';

	const [{ error: topicsError, loading: isTopicsLoading }, { fetchSuggestedTopics }] =
		useSuggestedTopics();
	const appEditions = useAppEditions();

	const isTopicSuggestionAllowed = useCallback(() => {
		return appEditions.serviceDesk === 'PREMIUM_EDITION';
	}, [appEditions.serviceDesk]);

	const showSuggestedTopics =
		isTopicSuggestionAllowed() &&
		!isAdminHubAIEnabledLoading &&
		!isAdminHubAIEnabledError &&
		isAdminHubAIEnabled &&
		!isTopicsLoading &&
		!topicsError;

	useEffect(() => {
		projectId &&
			isTopicSuggestionAllowed() &&
			isAdminHubAIEnabled &&
			fetchSuggestedTopics(projectId, createAnalyticsEvent({}), activationId);
	}, [
		activationId,
		createAnalyticsEvent,
		fetchSuggestedTopics,
		isTopicSuggestionAllowed,
		projectId,
		isAdminHubAIEnabled,
	]);

	useEffect(() => {
		isTopicSuggestionAllowed() && fetchAIEnablement();
	}, [fetchAIEnablement, isTopicSuggestionAllowed]);

	return (
		<>
			{showSuggestedTopics && (
				<LazyPage
					Page={LazyServicedeskKnowledgeHubSuggestedArticles}
					pageId="spa-apps/servicedesk/knowledge-hub/suggested-articles"
					shouldShowSpinner
				/>
			)}
		</>
	);
};

export const ServiceDeskKnowledgeHubSuggestedArticlesWithResource = (): React.JSX.Element => {
	const {
		data: isAdminHubAIEnabled,
		loading: isAdminHubAIEnabledLoading,
		error: isAdminHubAIEnabledError,
		fetchAIEnablement,
	} = useIsAdminHubAIEnabled('jira-servicedesk');

	const {
		data: topicsData,
		error: topicsError,
		loading: isTopicsLoading,
	} = useResource(kbTopicsResource);

	const showSuggestedTopics =
		topicsData?.topicSuggestionAllowed &&
		!isAdminHubAIEnabledLoading &&
		!isAdminHubAIEnabledError &&
		isAdminHubAIEnabled &&
		!isTopicsLoading &&
		!topicsError;

	useEffect(() => {
		topicsData?.topicSuggestionAllowed && fetchAIEnablement();
	}, [fetchAIEnablement, topicsData?.topicSuggestionAllowed]);

	return (
		<>
			{showSuggestedTopics && (
				<LazyPage
					Page={LazyServicedeskKnowledgeHubSuggestedArticles}
					pageId="spa-apps/servicedesk/knowledge-hub/suggested-articles"
					shouldShowSpinner
				/>
			)}
		</>
	);
};
